"use client";

import { useState, useMemo, useTransition, useCallback } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import ProductsListing from "@/components/products/products-listing";
import ComparisonTable from "@/components/products/comparison-table";
import { guniWheelData, guniWheelXData } from "@/lib/comparison-data";
import type { ProductDetailsInfo } from "@/lib/bagisto/types";

export type ActiveTab = "guni-wheel" | "guni-wheel-x";

interface ProductsClientWrapperProps {
  products: ProductDetailsInfo[];
}

const TAB_TO_CATEGORY_ID: Record<ActiveTab, string> = {
  "guni-wheel": "3",
  "guni-wheel-x": "4",
} as const;

function getTabFromParam(param: string | null): ActiveTab {
  return param === "guni-wheel-x" ? "guni-wheel-x" : "guni-wheel";
}

export default function ProductsClientWrapper({
  products: initialProducts,
}: ProductsClientWrapperProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const [activeTab, setActiveTab] = useState<ActiveTab>(
    getTabFromParam(searchParams.get("category"))
  );
  const [products, setProducts] = useState<ProductDetailsInfo[]>(initialProducts);
  const [isLoading, setIsLoading] = useState(false);

  const fetchProducts = useCallback(async (tab: ActiveTab) => {
    setIsLoading(true);
    try {
      const categoryId = TAB_TO_CATEGORY_ID[tab];
      const response = await fetch(
        `/api/products?categoryId=${categoryId}`,
        { cache: "no-store" }
      );

      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

      const data = await response.json();
      setProducts(data.products ?? []);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleTabChange = useCallback(
    (tab: ActiveTab) => {
      if (tab === activeTab) return;
      setActiveTab(tab);
      fetchProducts(tab);
      startTransition(() => {
        router.replace(`?category=${tab}`, { scroll: false });
      });
    },
    [router, fetchProducts, activeTab]
  );

  const comparisonData = useMemo(
    () => (activeTab === "guni-wheel" ? guniWheelData : guniWheelXData),
    [activeTab]
  );

  return (
    <div id="products">
      <ProductsListing
        products={products}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isLoading={isLoading || isPending}
      />
      <ComparisonTable data={comparisonData} />
    </div>
  );
}